import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Dropdown } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import { TemplateCard } from "../../pages/Settings/StyledComponents";
import { signatureBadge } from "./templateSignature";
import { governmentFormRibbon } from "./templateGovernmentForm";

// Sits across the top of the preview when VA has moved on from the form this
// template was built from (or the monthly check could not vouch for it).
const FormRibbon = styled.div`
  position: absolute;
  top: 8px;
  left: 0;
  right: 0;
  z-index: 2;
  padding: 3px 8px;
  text-align: center;
  font-family: "Manrope", sans-serif;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.25;
  color: #fff;
  background: ${(p) => (p.$tone === "alarm" ? "#dc2626" : "#d97706")};
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.25);
  pointer-events: auto;
  cursor: help;
  span {
    display: block;
    font-weight: 500;
    opacity: 0.95;
  }
`;

const SignatureBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 600;
  font-family: "Manrope", sans-serif;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: ${(p) => (p.$tone === "signable" ? "#ede9fe" : "#f3f4f6")};
  color: ${(p) => (p.$tone === "signable" ? "#5b21b6" : "#9ca3af")};
  border: 1px solid ${(p) => (p.$tone === "signable" ? "#ddd6fe" : "#e5e7eb")};
  &:hover {
    background: ${(p) => (p.$tone === "signable" ? "#ddd6fe" : "#e5e7eb")};
  }
`;

const MappingBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 600;
  font-family: "Manrope", sans-serif;
  cursor: pointer;
  transition: all 0.15s;
  background: ${(p) => (p.$count > 0 ? "#dbeafe" : "#f3f4f6")};
  color: ${(p) => (p.$count > 0 ? "#1d4ed8" : "#9ca3af")};
  border: 1px solid ${(p) => (p.$count > 0 ? "#bfdbfe" : "#e5e7eb")};
  &:hover {
    background: ${(p) => (p.$count > 0 ? "#bfdbfe" : "#e5e7eb")};
  }
`;

const PreviewFallback = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  color: #1f2937;
  flex-direction: column;
`;

const FileGlyph = styled.div`
  width: 86px;
  height: 118px;
  border-radius: 2px;
  border: 1px solid #d1d5db;
  background: #fff;
  position: relative;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
  overflow: hidden;
  &:before {
    content: "WHG";
    position: absolute;
    left: 50%;
    top: 8px;
    transform: translateX(-50%);
    font-size: 7px;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: #5b21b6;
  }
  &:after {
    content: "";
    position: absolute;
    left: 12px;
    right: 12px;
    top: 28px;
    height: 78px;
    background:
      linear-gradient(#9ca3af, #9ca3af) 0 0 / 100% 1px no-repeat,
      linear-gradient(#d1d5db, #d1d5db) 0 12px / 88% 1px no-repeat,
      linear-gradient(#d1d5db, #d1d5db) 0 24px / 96% 1px no-repeat,
      linear-gradient(#d1d5db, #d1d5db) 0 36px / 82% 1px no-repeat,
      linear-gradient(#d1d5db, #d1d5db) 0 48px / 94% 1px no-repeat,
      linear-gradient(#d1d5db, #d1d5db) 0 60px / 74% 1px no-repeat;
  }
`;

const FileTypeLabel = styled.div`
  position: absolute;
  right: 8px;
  bottom: 8px;
  border-radius: 999px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  padding: 1px 6px;
  font-size: 9px;
  font-weight: 800;
  color: #0c53bb;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const TemplateCardItem = ({
  template,
  openDropdownId,
  setOpenDropdownId,
  onDisable,
  onPreview,
  onEdit,
  onDelete,
  onDownload,
  showMappingBadge = false,
  onMoveCategory,
  isEnabled,
  fetchThumbnail,
  mode = "fileTemplates",
}) => {
  const [imageError, setImageError] = useState(false);
  const [fetchedThumbnail, setFetchedThumbnail] = useState(null);

  // Template APIs have used a few thumbnail names over time; prefer any image-specific URL before falling back.
  const thumbnailSrc =
    template?.thumbnail ||
    template?.thumbnail_url ||
    template?.preview_thumbnail_url ||
    template?.preview_image_url ||
    template?.preview_image ||
    template?.image_url ||
    template?.header_image_url ||
    fetchedThumbnail;
  const showPlaceholder = !thumbnailSrc || imageError;

  // Lazy-load thumbnail for letterheads when list API doesn't provide one
  useEffect(() => {
    if (!fetchThumbnail || thumbnailSrc || !template?.id) return;
    let cancelled = false;
    fetchThumbnail(template.id)
      .then((url) => {
        if (!cancelled && url) setFetchedThumbnail(url);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [template?.id, fetchThumbnail, thumbnailSrc]);
  const menuItems = [];
  const isLetterheadMode = mode === "letterhead";
  const signature = isLetterheadMode ? null : signatureBadge(template);
  const formRibbon = isLetterheadMode ? null : governmentFormRibbon(template);

  if (isLetterheadMode) {
    // Letterhead mode: Preview, Edit, Delete
    if (onEdit) {
      menuItems.push({
        key: "edit",
        label: "Edit letterhead details",
        onClick: () => onEdit(template),
      });
    }
    if (onPreview) {
      menuItems.push({
        key: "preview",
        label: "Preview letterhead",
        onClick: () => onPreview(template),
      });
    } 
    if (onDelete) {
      menuItems.push({
        key: "delete",
        label: "Delete letterhead",
        onClick: () => onDelete(template),
      });
    }
  } else {
    // File templates mode: Disable/Enable, Preview (original implementation)
    if (onDisable) {
      menuItems.push({
        key: "disable",
        label: `${isEnabled ? "Disable" : "Enable"} Template`,
        onClick: () => onDisable(template),
      });
    }
    
    if (onEdit) {
      menuItems.push({
        key: "edit",
        label: "Edit Template",
        onClick: () => onEdit(template),
      });
    }

    if (onPreview) {
      menuItems.push({
        key: "preview",
        label: "Preview",
        onClick: () => onPreview(template),
      });
    }

    if (onDownload) {
      menuItems.push({
        key: "download",
        label: "Download Template",
        onClick: () => onDownload(template),
      });
    }

    if (onMoveCategory) {
      menuItems.push({
        key: "move-category",
        label: "Move to another Category",
        onClick: () => onMoveCategory(template),
      });
    }

    if (onDelete) {
      menuItems.push({
        key: "delete",
        label: "Delete Template",
        danger: true,
        onClick: () => onDelete(template),
      });
    }
  }

  return (
    <TemplateCard key={template?.id}>
      {formRibbon && (
        <FormRibbon
          $tone={formRibbon.tone}
          title={formRibbon.hint}
          data-testid="template-form-ribbon"
          data-status={formRibbon.status}
          role="note"
        >
          {formRibbon.headline}
          {formRibbon.when && <span>{formRibbon.when}</span>}
        </FormRibbon>
      )}
      {showPlaceholder ? (
        <PreviewFallback
          className="preview preview-placeholder"
          data-testid="thumbnail-preview"
        >
          <FileGlyph />
          <FileTypeLabel>{template?.template_type || template?.file_type || "file"}</FileTypeLabel>
        </PreviewFallback>
      ) : (
        <img
          src={thumbnailSrc}
          alt="preview"
          className="preview"
          data-testid="thumbnail-preview"
          onError={() => setImageError(true)}
        />
      )}
      <div className="footer">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="template-name">{template?.name}</div>
          {!isLetterheadMode && showMappingBadge && (
            <MappingBadge
              $count={template?.mapped_field_count || 0}
              onClick={onEdit ? () => onEdit(template) : undefined}
              title={onEdit ? "Edit template" : undefined}
            >
              {template?.mapped_field_count > 0
                ? `${template.mapped_field_count} fields mapped`
                : "No mappings"}
            </MappingBadge>
          )}
          {signature && (
            <SignatureBadge
              $tone={signature.tone}
              $clickable={Boolean(onEdit)}
              onClick={onEdit ? () => onEdit(template) : undefined}
              title={signature.hint}
              data-testid="template-signature-badge"
              data-tone={signature.tone}
            >
              {signature.label}
            </SignatureBadge>
          )}
        </div>
        <Dropdown
          open={openDropdownId === template.id}
          onOpenChange={(open) => setOpenDropdownId(open ? template.id : null)}
          menu={{
            items: menuItems,
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <EllipsisOutlined className="menu-icon" data-testid="file-template-options"/>
        </Dropdown>
      </div>
    </TemplateCard>
  );
};

TemplateCardItem.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    thumbnail: PropTypes.string,
    thumbnail_url: PropTypes.string,
    preview_thumbnail_url: PropTypes.string,
    preview_image_url: PropTypes.string,
    preview_image: PropTypes.string,
    image_url: PropTypes.string,
    header_image_url: PropTypes.string,
  }).isRequired,
  openDropdownId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setOpenDropdownId: PropTypes.func.isRequired,
  onDisable: PropTypes.func,
  onPreview: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onDownload: PropTypes.func,
  showMappingBadge: PropTypes.bool,
  onMoveCategory: PropTypes.func,
  isEnabled: PropTypes.bool,
  fetchThumbnail: PropTypes.func,
  mode: PropTypes.oneOf(["fileTemplates", "letterhead"]),
};

export default TemplateCardItem;
